import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Progress } from "./ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Home, Sofa, UploadCloud, Download, Loader2, Sparkles, Share2, FolderPlus, Check, Trash2, ImageIcon, Ruler, Pin, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "../lib/apiClient";
import { compressImage, compressMany, MAX_UPLOAD_BYTES } from "../lib/imageCompress";
import { SAMPLE_BLUEPRINT, uid } from "../lib/blueprint";
import SpecSheetDialog from "./SpecSheetDialog";
import InteriorAssist from "./InteriorAssist";
import ConceptCompare from "./ConceptCompare";
import StyleBoard from "./StyleBoard";
import RoomCostDialog from "./RoomCostDialog";

const STYLES = ["modern", "farmhouse", "contemporary", "scandinavian", "industrial", "mediterranean"];
const SPACES = [
  { v: "porch", label: "Porch / Sunroom" },
  { v: "attic", label: "Attic Loft" },
  { v: "room", label: "Room" },
];
const cap = (s) => String(s || "").replace(/^\w/, (c) => c.toUpperCase());

export default function RedesignStudio({ open, mode = "exterior", onOpenChange }) {
  const isExt = mode === "exterior";
  const accent = isExt ? "#38BDF8" : "#F59E0B";
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [style, setStyle] = useState("modern");
  const [notes, setNotes] = useState("");
  const [space, setSpace] = useState("porch");
  const [width, setWidth] = useState("");
  const [length, setLength] = useState("");
  const [height, setHeight] = useState("");
  const [count, setCount] = useState(3);

  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null); // { redesign_id, original_path, variations[] }
  const [selIdx, setSelIdx] = useState(0);
  const [history, setHistory] = useState([]);
  const [acting, setActing] = useState("");
  const [showSpec, setShowSpec] = useState(false);
  const [assist, setAssist] = useState({});
  const [agentMode, setAgentMode] = useState(true);
  const [taste, setTaste] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [showBoard, setShowBoard] = useState(false);
  const [showCost, setShowCost] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [projects, setProjects] = useState([]);
  const [savingGallery, setSavingGallery] = useState("");

  const reset = () => {
    setFile(null); setPreview(null); setNotes(""); setResult(null); setSelIdx(0);
    setProgress(0); setBusy(false); setWidth(""); setLength(""); setHeight("");
  };

  const loadHistory = useCallback(() => {
    api.get(`/ai/redesigns?kind=${mode}`).then((r) => setHistory(r.data || [])).catch(() => {});
    if (mode === "interior") api.get("/ai/taste-profile").then((r) => setTaste(r.data?.top_terms || [])).catch(() => {});
  }, [mode]);

  useEffect(() => {
    if (open) { reset(); setStyle("modern"); setSpace("porch"); loadHistory(); }
    // eslint-disable-next-line
  }, [open, mode]);

  const pickFile = (f) => {
    if (!f) return;
    if (!/^image\//.test(f.type)) { toast.error("Please choose an image file"); return; }
    setFile(f); setPreview(URL.createObjectURL(f)); setResult(null);
  };

  const poll = (taskId) => new Promise((resolve, reject) => {
    let n = 0;
    const iv = setInterval(async () => {
      n += 1;
      setProgress((p) => Math.min(92, p + 6));
      try {
        const { data } = await api.get(`/ai/render-status/${taskId}`);
        if (data.status === "completed") { clearInterval(iv); resolve(data); }
        else if (data.status === "failed") { clearInterval(iv); reject(new Error(data.error || "Failed")); }
        else if (n > 40) { clearInterval(iv); reject(new Error("Timed out")); }
      } catch (e) { clearInterval(iv); reject(e); }
    }, 3000);
  });

  const generate = async () => {
    if (!file) { toast.error("Upload a photo first"); return; }
    setBusy(true); setProgress(4); setResult(null);
    try {
      // Shrink every image in the browser first so slow-connection uploads stay
      // small and never trip the gateway's request-body limit (was causing 520s).
      setOptimizing(true);
      const a = assist || {};
      const [mainFile, samples, angles, floorplan] = await Promise.all([
        compressImage(file),
        compressMany(a.samples || []),
        compressMany(a.angles || []),
        a.floorplan ? compressImage(a.floorplan, { maxEdge: 2200, quality: 0.9 }) : Promise.resolve(null),
      ]);
      setOptimizing(false);
      const tooBig = [mainFile, floorplan, ...samples, ...angles]
        .filter(Boolean).find((f) => f.size > MAX_UPLOAD_BYTES);
      if (tooBig) throw new Error("One image is still too large after optimizing — please pick a smaller photo.");

      setProgress(8);
      const fd = new FormData();
      fd.append("file", mainFile);
      const qs = new URLSearchParams({ style, notes, count: String(count) });
      let url;
      if (isExt) {
        url = `/ai/redesign-exterior?${qs.toString()}`;
      } else {
        qs.set("room", a.roomDesc || space || "room");
        if (a.brief) qs.set("brief", a.brief);
        if (a.width) qs.set("width", a.width);
        if (a.length) qs.set("length", a.length);
        if (a.height) qs.set("height", a.height);
        samples.forEach((sf) => fd.append("samples", sf));
        if (floorplan) fd.append("floorplan", floorplan);
        angles.forEach((af) => fd.append("angles", af));
        url = `/ai/${agentMode ? "design-agent" : "design-room"}?${qs.toString()}`;
      }
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const done = await poll(data.task_id);
      setProgress(100);
      const variations = done.variations && done.variations.length ? done.variations : [done.render_path];
      setResult({ redesign_id: done.redesign_id, original_path: done.original_path, variations, concepts: done.concepts || null });
      setSelIdx(0);
      loadHistory();
      toast.success(`${variations.length} design${variations.length > 1 ? "s" : ""} ready`);
    } catch (e) {
      toast.error(e.message || "Generation failed");
    } finally {
      setBusy(false); setOptimizing(false);
    }
  };

  const selectedPath = result ? result.variations[selIdx] : null;

  const chooseVariation = async (i) => {
    setSelIdx(i);
    if (result?.concepts && result?.redesign_id) {
      try {
        const { data } = await api.post("/ai/design-agent/feedback", { redesign_id: result.redesign_id, selected_index: i });
        if (data.top_terms) setTaste(data.top_terms);
        toast.success("Saved — the agent learned from your pick");
      } catch { /* non-blocking */ }
    }
  };

  const pinToBoard = async () => {
    if (!result?.redesign_id) return;
    setActing("pin");
    try {
      await api.post(`/ai/redesigns/${result.redesign_id}/pin`, { pinned: true });
      api.get("/ai/taste-profile").then((r) => setTaste(r.data?.top_terms || [])).catch(() => {});
      toast.success("Pinned to your Style Board");
    } catch { toast.error("Could not pin"); }
    finally { setActing(""); }
  };

  const download = async () => {
    if (!selectedPath) return;
    const src = fileUrl(selectedPath);
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    try {
      const res = await fetch(src);
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const supportsDownload = "download" in document.createElement("a");
      if (isMobile || !supportsDownload) {
        // iOS/Android Safari ignore a[download]; open image so user can long-press -> Save Image
        window.open(url, "_blank");
        toast.info("Press and hold the image, then tap “Save Image”");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `archimesh-${mode}-${style}.png`;
        document.body.appendChild(a); a.click(); a.remove();
        toast.success("Image saved");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      // last-resort fallback: open the raw URL in a new tab
      try { window.open(src, "_blank"); toast.info("Opened the image in a new tab — long-press or right-click to save"); }
      catch { toast.error("Download failed — try again"); }
    }
  };

  const share = async () => {
    if (!result) return;
    setActing("share");
    try {
      const { data } = await api.post(`/ai/redesigns/${result.redesign_id}/share`, { selected_path: selectedPath });
      const link = `${window.location.origin}/r/${data.share_id}`;
      try { await navigator.clipboard.writeText(link); toast.success("Before/after link copied to clipboard"); }
      catch { toast.success(link); }
    } catch { toast.error("Could not create share link"); }
    finally { setActing(""); }
  };

  const saveToProject = async () => {
    if (!selectedPath) return;
    setActing("save");
    try {
      const clone = JSON.parse(JSON.stringify(SAMPLE_BLUEPRINT));
      const rooms = (clone.rooms || []).map((r) => ({ ...r, id: uid() }));
      const items = (clone.items || []).map((it) => ({ ...it, id: uid() }));
      const blueprint = { floors: [{ name: "Ground Floor", rooms, items }], rooms, items };
      const payload = { name: `AI ${isExt ? "Exterior" : "Interior"} \u2014 ${cap(style)}`, render_path: selectedPath, blueprint, description: `Created from an AI ${isExt ? "exterior" : "interior"} redesign` };
      const { data } = await api.post("/projects", payload);
      toast.success("Saved as a new blueprint project");
      onOpenChange(false);
      navigate("/studio", { state: { projectId: data.id } });
    } catch { toast.error("Could not create project"); }
    finally { setActing(""); }
  };

  const openGalleryPicker = async () => {
    if (!selectedPath) return;
    try {
      const { data } = await api.get("/projects");
      setProjects(data || []);
      setShowGallery(true);
    } catch { toast.error("Could not load your projects"); }
  };

  const addToGallery = async (projectId) => {
    if (!selectedPath) return;
    setSavingGallery(projectId);
    try {
      await api.post(`/projects/${projectId}/gallery`, { path: selectedPath, label: `AI ${cap(style)}` });
      toast.success("Render added to the project gallery");
      setShowGallery(false);
    } catch { toast.error("Could not save to gallery"); }
    finally { setSavingGallery(""); }
  };

  const openHistory = (item) => {
    const variations = item.variations && item.variations.length ? item.variations : [item.result_path];
    const idx = Math.max(0, variations.indexOf(item.selected_path || item.result_path));
    setResult({ redesign_id: item.id, original_path: item.original_path, variations, concepts: item.concepts || null });
    setSelIdx(idx);
  };

  const removeHistory = async (e, id) => {
    e.stopPropagation();
    try { await api.delete(`/ai/redesigns/${id}`); setHistory((h) => h.filter((x) => x.id !== id)); if (result?.redesign_id === id) setResult(null); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0D1117] border-[#21262D] text-[#F0F6FC] max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="redesign-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-2xl">
            {isExt ? <Home className="w-6 h-6" style={{ color: accent }} /> : <Sofa className="w-6 h-6" style={{ color: accent }} />}
            {isExt ? "AI Exterior Redesign" : "AI Interior Design & Furnish"}
          </DialogTitle>
          <p className="text-[#8B949E] text-sm">
            {isExt
              ? "Upload a photo of an old house \u2014 AI renders 3 fresh exterior concepts. No blueprint needed."
              : "Upload a photo of a porch, attic or any room \u2014 AI designs and fully furnishes it in 3 concepts. Add measurements for a precise, to-scale result."}
          </p>
        </DialogHeader>

        {/* Upload */}
        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
          className="border-2 border-dashed border-[#30363D] rounded-xl p-6 text-center cursor-pointer hover:border-[#484F58] transition-colors"
          data-testid="redesign-dropzone"
        >
          {preview ? (
            <img src={preview} alt="upload" className="max-h-48 mx-auto rounded-lg object-contain" />
          ) : (
            <div className="py-4">
              <UploadCloud className="w-8 h-8 mx-auto mb-2 text-[#8B949E]" />
              <p className="text-sm">Drop a {isExt ? "house" : "porch / attic / room"} photo here</p>
              <p className="text-xs text-[#6E7681] mt-1">JPEG · PNG · WEBP</p>
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} data-testid="redesign-file-input" />
        </div>

        {/* Controls */}
        <div className="rounded-xl border border-[#21262D] p-4 space-y-4">
          <div className={`grid ${isExt ? "grid-cols-1" : "grid-cols-2"} gap-3`}>
            <div>
              <Label className="text-xs text-[#8B949E]">Style</Label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger className="mt-1 bg-[#0D1117] border-[#30363D]" data-testid="redesign-style-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
                  {STYLES.map((s) => <SelectItem key={s} value={s}>{cap(s)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!isExt && (
              <div>
                <Label className="text-xs text-[#8B949E]">Space type</Label>
                <Select value={space} onValueChange={setSpace}>
                  <SelectTrigger className="mt-1 bg-[#0D1117] border-[#30363D]" data-testid="redesign-space-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
                    {SPACES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {!isExt && (
            <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-3" data-testid="design-agent-box">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#F59E0B]" />
                  <span className="text-sm font-heading font-semibold">Design Agent</span>
                  <span className="text-[10px] text-[#8B949E]">plans · thinks bold · learns your taste</span>
                </div>
                <button onClick={() => setAgentMode((v) => !v)} type="button" data-testid="agent-toggle"
                  className={`relative w-11 h-6 rounded-full transition-colors ${agentMode ? "bg-[#F59E0B]" : "bg-[#30363D]"}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${agentMode ? "translate-x-5" : ""}`} />
                </button>
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                {taste.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 items-center" data-testid="taste-terms">
                    <span className="text-[10px] text-[#8B949E] uppercase tracking-widest">Learned taste:</span>
                    {taste.slice(0, 6).map((t, i) => (
                      <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#161B22] border border-[#30363D] text-[#F59E0B]">{t}</span>
                    ))}
                  </div>
                ) : <span className="text-[10px] text-[#6E7681]">Picks train the agent's taste.</span>}
                <button onClick={() => setShowBoard(true)} type="button" className="text-[11px] text-[#F59E0B] hover:underline shrink-0 whitespace-nowrap" data-testid="style-board-btn">Style Board →</button>
              </div>
            </div>
          )}

          {!isExt && (
            <InteriorAssist roomFile={file} style={style} accent={accent} onChange={setAssist} />
          )}

          <div>
            <Label className="text-xs text-[#8B949E]">Options per run</Label>
            <div className="grid grid-cols-3 gap-2 mt-1" data-testid="count-toggle">
              {[1, 3, 6].map((n) => (
                <button key={n} onClick={() => setCount(n)} type="button"
                  className={`py-2 rounded-md text-sm font-semibold border transition-colors ${count === n ? "text-[#0D1117]" : "text-[#8B949E] border-[#30363D] hover:border-[#484F58]"}`}
                  style={count === n ? { background: accent, borderColor: accent } : {}}
                  data-testid={`count-${n}`}>
                  {n} {n === 1 ? "option" : "options"}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs text-[#8B949E]">Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder={isExt ? "e.g. black metal roof, white stucco, big front windows" : "e.g. cozy reading nook, warm wood, lots of plants"}
              className="mt-1 bg-[#0D1117] border-[#30363D] resize-none" data-testid="redesign-notes" />
          </div>
        </div>

        {busy && (
          <div className="space-y-2">
            <Progress value={progress} className="h-2 bg-[#161B22]" />
            <p className="text-xs text-[#8B949E] text-center">
              {optimizing ? "Optimizing your photos for a fast upload…" : "Generating 3 concepts… this takes ~20–40s"}
            </p>
          </div>
        )}

        <Button onClick={generate} disabled={busy || !file} className="w-full font-semibold text-[#0D1117]" style={{ background: accent }} data-testid="redesign-generate-btn">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {optimizing ? "Optimizing…" : "Working…"}</> : <><Sparkles className="w-4 h-4 mr-2" /> {isExt ? "Redesign Exterior" : "Design & Furnish"}</>}
        </Button>

        {/* Result: 3 variations */}
        {result && (
          <div className="space-y-3" data-testid="redesign-result">
            <div className="grid grid-cols-2 gap-3">
              <figure className="rounded-lg overflow-hidden border border-[#21262D]">
                <img src={fileUrl(result.original_path)} alt="before" className="w-full h-36 object-cover" data-testid="result-before" />
                <figcaption className="text-[10px] uppercase tracking-widest text-[#6E7681] px-2 py-1">Before</figcaption>
              </figure>
              <figure className="rounded-lg overflow-hidden border-2" style={{ borderColor: accent }}>
                <img src={fileUrl(selectedPath)} alt="after" className="w-full h-36 object-cover" data-testid="result-after" />
                <figcaption className="text-[10px] uppercase tracking-widest px-2 py-1 font-semibold" style={{ color: accent }}>After</figcaption>
              </figure>
            </div>

            {result.variations.length > 1 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-[#8B949E]">Choose your favourite ({result.variations.length} options)</p>
                  {result.concepts && (
                    <button onClick={() => setShowCompare(true)} className="text-[11px] text-[#F59E0B] hover:underline flex items-center gap-1" data-testid="compare-concepts-btn">
                      <Sparkles className="w-3 h-3" /> Compare concepts
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {result.variations.map((p, i) => (
                    <button key={p} onClick={() => chooseVariation(i)}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all ${i === selIdx ? "" : "border-transparent opacity-70 hover:opacity-100"}`}
                      style={{ borderColor: i === selIdx ? accent : "transparent" }} data-testid={`variation-${i}`}>
                      <img src={fileUrl(p)} alt={`option ${i + 1}`} className="w-full h-20 object-cover" />
                      {result.concepts && result.concepts[i] && (
                        <span className="absolute top-1 left-1 flex items-center gap-1">
                          {result.concepts[i].bold && <span className="text-[8px] px-1 py-0.5 rounded bg-[#F59E0B] text-[#0D1117] font-bold">BOLD</span>}
                          <span className="text-[9px] px-1 py-0.5 rounded bg-black/70 text-white max-w-[80px] truncate">{result.concepts[i].name}</span>
                        </span>
                      )}
                      {i === selIdx && <span className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-[#0D1117]" style={{ background: accent }}><Check className="w-3 h-3" /></span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={download} variant="outline" className="border-[#30363D] flex-1" data-testid="download-btn"><Download className="w-4 h-4 mr-1" /> Download</Button>
              <Button onClick={() => setShowSpec(true)} variant="outline" className="border-[#34D399]/50 text-[#34D399] hover:text-[#34D399] hover:border-[#34D399] flex-1" data-testid="spec-sheet-btn"><Ruler className="w-4 h-4 mr-1" /> Measured Spec (PDF)</Button>
              <Button onClick={share} disabled={acting === "share"} variant="outline" className="border-[#30363D] flex-1" data-testid="share-btn">
                {acting === "share" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Share2 className="w-4 h-4 mr-1" />} Share Before/After
              </Button>
              <Button onClick={saveToProject} disabled={acting === "save"} variant="outline" className="border-[#30363D] flex-1" data-testid="save-project-btn">
                {acting === "save" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FolderPlus className="w-4 h-4 mr-1" />} Save to Project
              </Button>
              <Button onClick={openGalleryPicker} variant="outline" className="border-[#30363D] flex-1" data-testid="save-gallery-btn">
                <ImageIcon className="w-4 h-4 mr-1" /> Save to Gallery
              </Button>
              {!isExt && (
                <Button onClick={pinToBoard} disabled={acting === "pin"} variant="outline" className="border-[#F59E0B]/50 text-[#F59E0B] hover:text-[#F59E0B] hover:border-[#F59E0B] flex-1" data-testid="pin-board-btn">
                  {acting === "pin" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Pin className="w-4 h-4 mr-1" />} Pin to Board
                </Button>
              )}
              {!isExt && (
                <Button onClick={() => setShowCost(true)} variant="outline" className="border-[#34D399]/40 text-[#34D399] hover:text-[#34D399] hover:border-[#34D399] flex-1" data-testid="room-cost-btn">
                  <DollarSign className="w-4 h-4 mr-1" /> Materials & Cost
                </Button>
              )}
            </div>
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#6E7681] mb-2 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Your saved designs</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {history.map((h) => (
                <button key={h.id} onClick={() => openHistory(h)} className="relative shrink-0 group" data-testid={`history-${h.id}`}>
                  <img src={fileUrl(h.selected_path || h.result_path)} alt="saved" className="w-24 h-16 object-cover rounded-lg border border-[#21262D] group-hover:border-[#484F58]" />
                  <span onClick={(e) => removeHistory(e, h.id)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 hidden group-hover:flex items-center justify-center hover:bg-red-500/80" data-testid={`history-del-${h.id}`}>
                    <Trash2 className="w-3 h-3 text-white" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
      <SpecSheetDialog
        open={showSpec}
        onOpenChange={setShowSpec}
        imagePath={selectedPath}
        kind={mode}
        title={`AI ${cap(mode)} \u2014 ${cap(style)}`}
        subtitle={isExt ? "Exterior concept" : cap(space)}
        known={isExt ? {} : { width: assist.width, length: assist.length, height: assist.height }}
        concept={!isExt && result?.concepts ? result.concepts[selIdx] : null}
      />
      <ConceptCompare
        open={showCompare}
        onOpenChange={setShowCompare}
        variations={result?.variations || []}
        concepts={result?.concepts || []}
        selIdx={selIdx}
        accent={accent}
        onChoose={(i) => { chooseVariation(i); setShowCompare(false); }}
      />
      <StyleBoard open={showBoard} onOpenChange={setShowBoard} onTasteChange={setTaste} />
      <RoomCostDialog open={showCost} onOpenChange={setShowCost} redesignId={result?.redesign_id} selectedIndex={selIdx} />
      <Dialog open={showGallery} onOpenChange={setShowGallery}>
        <DialogContent className="bg-[#0D1117] border-[#21262D] text-[#F0F6FC] max-w-md" data-testid="gallery-picker-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <ImageIcon className="w-5 h-5" style={{ color: accent }} /> Save render to a project gallery
            </DialogTitle>
            <p className="text-[#8B949E] text-sm">Pick which project's public bundle page this render should appear in.</p>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto space-y-2">
            {projects.length === 0 ? (
              <p className="text-sm text-[#6E7681] text-center py-6">You don't have any saved projects yet.</p>
            ) : projects.map((p) => (
              <button key={p.id} onClick={() => addToGallery(p.id)} disabled={!!savingGallery}
                className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border border-[#21262D] hover:border-[#484F58] text-left transition-colors disabled:opacity-60"
                data-testid={`gallery-project-${p.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{p.name}</p>
                  <p className="text-[11px] text-[#6E7681] truncate">{[p.city, p.state].filter(Boolean).join(", ") || "Custom"}</p>
                </div>
                {savingGallery === p.id
                  ? <Loader2 className="w-4 h-4 animate-spin shrink-0" style={{ color: accent }} />
                  : <FolderPlus className="w-4 h-4 shrink-0" style={{ color: accent }} />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
